import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

const LABELS = {
  transactions: "Bills", points_ledger: "Points entries", coupon_redemptions: "Coupon redemptions",
  coupons: "Coupons", nps_responses: "NPS responses", support_tickets: "Support tickets",
  "customers.home_store_id": "Customers (home store)", "customers.registered_store_id": "Customers (registered store)",
  store_logins: "Store logins (will be switched off)",
};

export const StoreDeleteDialog = ({ store, stores, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/stores/${store.id}/delete-preview`).then((r) => {
      setPreview(r.data);
      setTarget(r.data.suggested_target?.id || "");
    }).catch((e) => { toast.error(e?.response?.data?.detail || "Could not load store usage"); onClose(); });
  }, [store.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsTarget = preview && Object.entries(preview.counts).some(([k, v]) => k !== "store_logins" && v > 0);
  const others = stores.filter((s) => s.id !== store.id);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/stores/${store.id}`, { params: target ? { target_store_id: target } : {} });
      const n = r.data.moved?.transactions || 0;
      toast.success(`Deleted ${store.code}${r.data.moved_to ? ` — ${n.toLocaleString()} bills moved to ${r.data.moved_to.code}` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="store-delete-dialog">
      <div className="bg-white p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-1">Delete store</h3>
        <div className="text-sm text-neutral-600 mb-4"><span className="font-mono">{store.code}</span> · {store.name}{store.city ? ` · ${store.city}` : ""}</div>
        {!preview ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Checking what is linked to this store…</div>
        ) : (
          <>
            <table className="w-full text-sm mb-4" data-testid="store-delete-counts">
              <tbody>
                {Object.entries(preview.counts).map(([k, v]) => (
                  <tr key={k} className="border-b border-black/5"><td className="py-1 text-neutral-600">{LABELS[k] || k}</td><td className="py-1 text-right font-mono" data-testid={`store-delete-count-${k}`}>{v.toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
            {needsTarget ? (
              <label className="block text-sm mb-4">
                <span className="text-xs uppercase tracking-widest text-neutral-500">Move everything to</span>
                <select className="k-input w-full mt-1" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="store-delete-target">
                  <option value="">Select the correct store…</option>
                  {others.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}{s.city ? ` (${s.city})` : ""}</option>)}
                </select>
                {preview.suggested_target && <span className="text-xs text-emerald-700 mt-1 block" data-testid="store-delete-suggested">Suggested by matching name: {preview.suggested_target.code}</span>}
              </label>
            ) : (
              <div className="text-sm text-neutral-600 mb-4">Nothing is linked to this store — it can be removed directly.</div>
            )}
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 mb-4">
              <AlertTriangle className="w-4 h-4 shrink-0" /> This permanently removes the store. The action is recorded in the audit log.
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button className="k-btn k-btn-ghost" onClick={onClose} data-testid="store-delete-cancel">Cancel</button>
          <button className="k-btn kazo-bg-burgundy" disabled={!preview || busy || (needsTarget && !target)} onClick={confirm} data-testid="store-delete-confirm">
            {busy ? "Deleting…" : needsTarget ? "Move & delete" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};
